import {
  resolveCardChatId,
  type ActionCardAnalyticsContext,
  type ActionCardEventProps,
  type ActionCardSurface,
} from '@littlebird/action-cards';
import {
  ActionCard,
  getSDK,
  HttpError,
  isChatStatusBusy,
  MAIN_CHAT_MODE,
  type JSONObject,
  type RunRefusal,
} from '@littlebird/sdk';
import { isOpenableUrl } from '@littlebird/ui-utils';
import { toast } from 'sonner';

import {
  actionCardAnalyticsContext,
  actionCardAnalyticsSnapshot,
  operationId,
  trackCardAction,
  trackCardCommandFailed,
  type ActionCardActionTrigger,
  type ActionCardCommand,
} from '@/features/action-cards/analytics';
import { actionCardCommands } from '@/features/action-cards/commands';
import { performAttributedSend } from '@/features/chat/chat-send';
import { isLiveThread } from '@/features/chat/live-thread';
import { type OpenTab } from '@/features/tabs';
import { logError } from '@/lib/errorLogging';
import { NEW_CHAT_ID } from '@/utils/constants';
import { openUntrustedUrl } from '@/utils/external-links';
import { openUntrustedUrlIn, reserveTabForLink } from '@/utils/link-tab';

import {
  trackCardChatStarted,
  actionCardEventSnapshot,
  trackCardCompletedSnapshot,
} from './analytics';

import type {
  CardRow,
  CardState,
  RunNotCompleted,
  RunRefused,
} from '@littlebird/action-cards-view';

type ValidationErrors = CardState['validation_errors'];
type CardChanged = { card_changed: CardRow };

const REQUIRED_FIELD_MESSAGE = 'Fill this in first';
const REQUIRED_FIELDS_MESSAGE = 'Fill in the required fields first';

// These refusals are about the card's own fields, so a newer row would not lift them
const NEEDS_AN_EDIT: ReadonlySet<RunRefusal['kind']> = new Set([
  'required_empty',
  'not_runnable',
]);

// A refusal that is not about the fields means this client holds a stale row
const syncAfterRefusal = (cardId: string): void => {
  getSDK()
    .syncNow()
    .catch((error: unknown) =>
      logError(error, 'Failed to sync after a refused card write', {
        context: 'CardV2:syncAfterRefusal',
        metadata: { cardId },
      })
    );
};

const runRefusalErrors = (refusal: RunRefusal): ValidationErrors => {
  if (refusal.kind !== 'required_empty') {
    return { fields: {}, card: refusal.message };
  }
  return {
    fields: Object.fromEntries(
      refusal.fields.map((key) => [key, REQUIRED_FIELD_MESSAGE])
    ),
    card: refusal.fields.length === 0 ? REQUIRED_FIELDS_MESSAGE : null,
  };
};

// A rejection ends the renderer's optimistic running; the toast says why
const toastAndRethrow =
  (message: string, onRejected?: (error: unknown) => void) =>
  (error: unknown): never => {
    onRejected?.(error);
    toast.error(message);
    throw error;
  };

// A refusal the server answers with a code is the server's to record, as `action_card_command_refused`
const trackUnlessRefused =
  (analytics: ActionCardEventProps, command: ActionCardCommand) =>
  (error: unknown): void => {
    if (
      error instanceof HttpError &&
      typeof error.detail?.['error_code'] === 'string'
    )
      return;
    trackCardCommandFailed(analytics, command);
  };

const RUN_NOT_COMPLETED: RunNotCompleted = { not_completed: true };

// `seenSyncId` is the version of the card the user pressed on: an agent write after it (`agent_written_sync_id`)
// has the press refused and the card redrawn
export const runCard = async (
  card: ActionCard,
  key: string,
  seenSyncId: number,
  surface: ActionCardSurface,
  context = actionCardAnalyticsContext(surface),
  analyticsProps = actionCardEventSnapshot(card, context)
): Promise<void | RunRefused | RunNotCompleted> => {
  const outcome = await getSDK()
    .stores.actionCards.runToCompletion(card.id, key, { seenSyncId })
    .catch(
      toastAndRethrow(
        "Couldn't start this — check your connection and try again.",
        trackUnlessRefused(analyticsProps, 'run')
      )
    );
  if (outcome instanceof ActionCard) {
    // Only a completed run is a success. A failed run, or one still going when polling stopped, ends the press as not
    // completed: the card says why a run failed, and sync delivers how a running one ends. The card's session then stops
    // showing the press as running, which it would otherwise do until a new row arrived, so Retry works even when the
    // next failure leaves the row as it was
    if (outcome.status !== 'completed') return RUN_NOT_COMPLETED;
    trackCardCompletedSnapshot(analyticsProps, 'run', outcome.status);
    return;
  }
  if (!NEEDS_AN_EDIT.has(outcome.kind)) syncAfterRefusal(card.id);
  return { validation_errors: runRefusalErrors(outcome) };
};

export const detachCard = async (
  card: ActionCard,
  surface: ActionCardSurface = 'home'
): Promise<void> => {
  const context = actionCardAnalyticsContext(surface);
  const analytics = actionCardAnalyticsSnapshot(card, context);
  const operation = operationId();
  trackCardAction('detach', analytics, 'submitted', operation);
  try {
    await getSDK().stores.actionCards.detach(card.id);
    trackCardAction('detach', analytics, 'queued', operation);
  } catch (error) {
    trackCardAction('detach', analytics, 'failed', operation, {
      outcomeStatus: 'error',
    });
    throw toastAndRethrow("Couldn't move this card.")(error);
  }
};

const OPENS_THROUGH_SERVER: ReadonlySet<ActionCard['status']> = new Set([
  'draft',
  'failed',
]);

const openThroughServer = async (
  card: ActionCard,
  tab: Window | null,
  saveEdits: () => Promise<boolean>
): Promise<void> => {
  // The card shows why a save failed or was refused, so the app is not sent the card without the edit
  if (!(await saveEdits())) {
    tab?.close();
    return;
  }
  const outcome = await getSDK().stores.actionCards.openInApp(card.id);
  if ('kind' in outcome) {
    tab?.close();
    toast.error(outcome.message);
    if (outcome.kind === 'card_changed') syncAfterRefusal(card.id);
    return;
  }
  if (!outcome.url) {
    tab?.close();
    return;
  }
  openUntrustedUrlIn(tab, outcome.url);
};

const nothingToSave = async () => true;

/**
 * The card's own "Open in ‹app›" link opens through the server while the card is pending, which first makes the
 * app hold the card as it stands, such as a Gmail draft, once `saveEdits` has saved what the user typed. Any
 * other link opens as it is
 */
export const openCardLink = (
  card: ActionCard,
  url: string,
  analytics: ActionCardEventProps,
  saveEdits: () => Promise<boolean> = nothingToSave
): void => {
  if (
    url !== card.facts['open_url'] ||
    !OPENS_THROUGH_SERVER.has(card.status) ||
    // A link that cannot open is refused before a tab opens for it
    !isOpenableUrl(url)
  ) {
    openUntrustedUrl(url);
    return;
  }
  const tab = reserveTabForLink();
  openThroughServer(card, tab, saveEdits).catch((error: unknown) => {
    trackUnlessRefused(analytics, 'open_in_app')(error);
    tab?.close();
    toast.error("Couldn't open this — check your connection and try again.");
    logError(error, 'Failed to open an action card in its app', {
      context: 'CardV2:openCardLink',
      metadata: { cardId: card.id },
    });
  });
};

export const restoreCard = async (
  card: ActionCard,
  surface: ActionCardSurface = 'web_feeds',
  context = actionCardAnalyticsContext(surface),
  // Set when the restore takes back what the user just did, from that toast's Undo
  trigger?: ActionCardActionTrigger
): Promise<void> => {
  const analytics = actionCardAnalyticsSnapshot(card, context);
  const operation = operationId();
  trackCardAction('restore', analytics, 'submitted', operation, { trigger });
  try {
    await getSDK().stores.actionCards.restore(card.id);
    trackCardAction('restore', analytics, 'queued', operation, { trigger });
  } catch (error) {
    trackCardAction('restore', analytics, 'failed', operation, {
      trigger,
      outcomeStatus: 'error',
    });
    throw toastAndRethrow("Couldn't restore this card.")(error);
  }
};

// A card changed since this client last read it shows its newer row; the edit is lost
const newerRowOf = async (cardId: string): Promise<CardChanged | undefined> => {
  const fresh = await getSDK()
    .stores.actionCards.getFresh(cardId)
    .catch((error: unknown) => {
      logError(error, 'Failed to fetch a changed card', {
        context: 'CardV2:newerRowOf',
        metadata: { cardId },
      });
      return null;
    });
  if (!fresh) return undefined;
  toast.error("This card changed, so your edit wasn't saved.");
  return { card_changed: fresh.row };
};

export const commitCardFields = async (
  card: ActionCard,
  fields: Record<string, unknown>,
  onSaved?: (savedSyncId: number) => void,
  analytics?: ActionCardEventProps
): Promise<CardRow | RunRefused | CardChanged> => {
  // The view state adds derived keys. Most are display-only, but the server lists the ones it takes edits to
  const derived = card.computed?.fields ?? {};
  const editable = new Set(card.computed?.editable ?? []);
  const stored = Object.fromEntries(
    Object.entries(fields).filter(
      ([key]) =>
        key !== 'type' &&
        (key in card.content || editable.has(key) || !(key in derived))
    )
  );
  const outcome = await getSDK()
    .stores.actionCards.replaceFields(card.id, stored as JSONObject)
    .catch(
      toastAndRethrow(
        'Failed to save changes.',
        analytics && trackUnlessRefused(analytics, 'save_fields')
      )
    );
  if (!('kind' in outcome)) {
    // The revision of this save's own answer, which the store may already have moved past
    onSaved?.(outcome.savedSyncId);
    return outcome.card.row;
  }
  if (outcome.kind === 'validation') {
    return {
      validation_errors: { fields: outcome.fields, card: outcome.card },
    };
  }
  if (outcome.kind === 'card_changed') {
    const changed = await newerRowOf(card.id);
    if (changed) return changed;
  }
  syncAfterRefusal(card.id);
  return { validation_errors: { fields: {}, card: outcome.message } };
};

// Stored before the first await, so a double press sends once and later presses reopen the same chat
const startedChats = new Map<string, Promise<string | undefined>>();

type ChatSend = {
  readonly chatId: string | undefined;
  // Rejects when the message was never created. Resolves only once the reply has streamed
  readonly settled: Promise<unknown>;
};

// The chat opens before its message is created, so a send that fails then reaches no caller's catch
const reportingUnsent =
  (analytics: ActionCardEventProps) =>
  (send: ChatSend): ChatSend => ({
    ...send,
    settled: send.settled.catch((error: unknown) => {
      trackCardCommandFailed(analytics, 'start_chat');
      throw error;
    }),
  });

const startChat = (
  cardId: string,
  send: () => Promise<ChatSend>
): Promise<string | undefined> => {
  const sending = send();
  const started = sending.then(({ chatId }) => chatId);
  startedChats.set(cardId, started);
  // A failed send is not a started chat, and neither is a message that was never created; the next press tries again
  void sending
    .then(({ settled }) => settled)
    .catch(() => {
      if (startedChats.get(cardId) === started) startedChats.delete(cardId);
    });
  return started;
};

const startChatOnce = async (
  cardId: string,
  send: () => Promise<ChatSend>,
  existingChatId: string | null
): Promise<string | undefined> => {
  const previous = startedChats.get(cardId);
  // The parent may have started a shared conversation since this card started its own
  if (
    previous &&
    existingChatId &&
    (await previous) !== existingChatId &&
    startedChats.get(cardId) === previous
  )
    startedChats.delete(cardId);
  const chatId = await (startedChats.get(cardId) ?? startChat(cardId, send));
  if (!chatId) startedChats.delete(cardId);
  return chatId;
};

type SendOptions = {
  mode?: string;
  agent?: string;
  followUpCardId?: string;
  originatingCardId?: string;
  surface?: ActionCardSurface;
  context?: ActionCardAnalyticsContext;
};

// Sends into `chatId`, or into a new chat when it is null
const sendChatMessage = async (
  chatId: string | null,
  content: string,
  {
    mode = MAIN_CHAT_MODE,
    agent,
    followUpCardId,
    originatingCardId,
    surface = 'chat',
    context = actionCardAnalyticsContext(surface),
  }: SendOptions = {}
): Promise<ChatSend> => {
  const dispatch = await performAttributedSend({
    threadId: chatId ?? NEW_CHAT_ID,
    mode,
    agent,
    content,
    requestResponseAudio: false,
    originatingCardId,
    cardAnalyticsContext: context,
    prepareExtras: followUpCardId
      ? () => ({ followUpActionCardIds: [followUpCardId] })
      : undefined,
  });
  const settled =
    'sendPromise' in dispatch ? dispatch.sendPromise : Promise.resolve();
  return {
    chatId: dispatch.createdThreadId ?? chatId ?? undefined,
    settled,
  };
};

export type ChatParams = { prompt: string; mode?: string; agent?: string };

export const startChatFromCard = async (
  card: ActionCard,
  params: ChatParams,
  {
    openTab,
    surface,
    context = actionCardAnalyticsContext(surface),
  }: {
    openTab: OpenTab;
    surface: ActionCardSurface;
    context?: ActionCardAnalyticsContext;
  }
): Promise<void> => {
  const sharedChatId = card.parentId ? familyChatOf(card) : null;
  if (
    sharedChatId &&
    isChatStatusBusy(getSDK().stores.threads.getById(sharedChatId)?.status)
  ) {
    openTab({ path: `/chats/${sharedChatId}` });
    return;
  }
  const completionProps = actionCardEventSnapshot(card, context);
  const chatId = await startChatOnce(
    card.id,
    () => {
      trackCardChatStarted(card, surface, context);
      return sendChatMessage(sharedChatId, params.prompt, {
        ...params,
        ...(sharedChatId ? { followUpCardId: card.id } : {}),
        originatingCardId: card.id,
        surface,
        context,
      }).then(reportingUnsent(completionProps));
    },
    sharedChatId
  );
  if (!chatId) return;
  // Every press retries the completion; the endpoint is idempotent
  void actionCardCommands
    .completeExternally(card.id)
    .then((outcome) => {
      const updated = getSDK().stores.actionCards.get(card.id);
      if (
        outcome.status === 'succeeded' &&
        updated &&
        (updated.status === 'completed' ||
          updated.status === 'completed_externally')
      )
        trackCardCompletedSnapshot(
          completionProps,
          'chat_handoff',
          updated.status
        );
    })
    .catch((error: unknown) =>
      logError(error, 'Failed to mark a card done after starting its chat', {
        context: 'CardV2:chat',
        metadata: { cardId: card.id },
      })
    );
  openTab({ path: `/chats/${chatId}` });
};

// The attached card carries its title and failure, so the seed never repeats a raw error as the user's words
export const cardChatSeed = (card: ActionCard): string =>
  card.status === 'failed'
    ? "This didn't go through. Can you fix it and try again?"
    : 'Can you help me with this?';

// The new chat's composer holds the card, so the user writes the first message
export const openCardChatTab = (
  card: ActionCard,
  openTab: OpenTab,
  openPlainCardChat?: () => void
): void => {
  const chatId = familyChatOf(card) ?? card.followUpChatId;
  if (chatId) openTab({ path: `/chats/${chatId}` });
  else if (openPlainCardChat) openPlainCardChat();
  else openTab({ path: '/chats', search: { source_action_card: card.id } });
};

// The card is attached to the message, so the agent sees all of it. The card stays open for a retry,
// and a to-do's own button passes its prompt as the first message. The chat opens without waiting for the reply
export const startCardChat = async (
  card: ActionCard,
  openTab: OpenTab,
  prompt: string = cardChatSeed(card),
  context = actionCardAnalyticsContext('chat')
): Promise<void> => {
  const existingChatId = familyChatOf(card);
  if (
    existingChatId &&
    isChatStatusBusy(getSDK().stores.threads.getById(existingChatId)?.status)
  ) {
    openTab({ path: `/chats/${existingChatId}` });
    return;
  }
  const chatId = await startChatOnce(
    card.id,
    () =>
      sendChatMessage(existingChatId, prompt, {
        followUpCardId: card.id,
        originatingCardId: card.id,
        surface: context.surface,
        context,
      }).then(reportingUnsent(actionCardEventSnapshot(card, context))),
    existingChatId
  );
  if (chatId) openTab({ path: `/chats/${chatId}` });
};

// A family shares its parent's chat, even if a subtask previously started another one
const familyChatOf = (card: ActionCard): string | null => {
  const { actionCards, threads } = getSDK().stores;
  return resolveCardChatId(card, {
    getCard: (id) => actionCards.get(id),
    getChildren: (parentId) => actionCards.getChildren(parentId),
    isLiveChat: (candidate) => isLiveThread(threads, candidate),
  });
};
